import { useState } from "react";
import { Link } from "react-router-dom";
import { requestFaucet } from "@/lib/api";
import { FAUCET } from "@/constants/testIds";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LOGO_URL } from "@/constants/network";
import { useWallet } from "@/contexts/WalletContext";
import { Droplets, Loader2, CheckCircle, AlertCircle, PlusCircle, Timer } from "lucide-react";
import { useFaucetCooldown, formatCountdown } from "@/hooks/useFaucetCooldown";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { FaucetMonitor } from "@/components/faucet/FaucetMonitor";
import { TestTokenNote } from "@/components/common/TestTokenNote";
import { AddTokenButtons } from "@/components/faucet/AddTokenButtons";
import { FADE_UP_SLOW } from "@/lib/motion";

const SubmitLabel = ({ loading, waitSeconds, token, amount }) => {
  if (loading) {
    return <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Sending {token}...</span>;
  }
  if (waitSeconds > 0) {
    return (
      <span data-testid="faucet-cooldown-timer" className="flex items-center gap-2 font-mono">
        <Timer size={16} /> Next {token} claim in {formatCountdown(waitSeconds)}
      </span>
    );
  }
  return <span className="flex items-center gap-2"><Droplets size={16} /> Request {amount} {token}</span>;
};

const RESULT_ANIM = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

export default function FaucetPage() {
  const [address, setAddress] = useState("");
  const [token, setToken] = useState("FMI");
  const amount = token === "FMI" ? 10 : 100;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const { address: walletAddr, addFSCNetwork } = useWallet();
  const queryClient = useQueryClient();
  const { remainingFor, refetch: refetchCooldown } = useFaucetCooldown(address);
  const waitSeconds = remainingFor(token);

  const fillWalletAddress = () => {
    if (walletAddr) setAddress(walletAddr);
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    const addr = address.trim();
    if (!addr.startsWith("0x") || addr.length !== 42) {
      setError("Please enter a valid Ethereum address (0x...)");
      return;
    }
    setLoading(true);
    try {
      const data = await requestFaucet(addr, token);
      setResult(data);
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ["faucetStatus"] });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["faucetStatus"] }), 4000);
    } catch (err) {
      const msg = err.response?.data?.detail || "Request failed. Try again later.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      refetchCooldown();
    }
  };

  return (
    <div data-testid={FAUCET.container} className="max-w-xl mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP_SLOW}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-4">
            <Droplets size={28} className="text-[#D4AF37]" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            FSC <span className="text-[#D4AF37]">Faucet</span>
          </h1>
          <p className="text-zinc-400">
            Get free FMI, USDT and FBTC on the live FMI Smart Chain
          </p>
          <TestTokenNote testId="faucet-test-token-note" className="mt-3" />
          <div className="mt-4">
            <Button data-testid="faucet-add-network-btn" variant="outline" size="sm" onClick={addFSCNetwork}
              className="border-[#2A2411] bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F3E5AB]">
              <PlusCircle size={14} className="mr-1.5" /> Add FSC to wallet
            </Button>
          </div>
        </div>

        <Card className="border-[#2A2411] bg-[#0A0A0A] gold-glow">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#050505] border border-[#2A2411]">
              <img src={LOGO_URL} alt="FMI" className="w-8 h-8 rounded-full" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">10 FMI, 100 USDT or 0.01 FBTC per request</p>
                <p className="text-xs text-zinc-500">Rate limit: once every 5 minutes per address, per token</p>
              </div>
            </div>

            <div data-testid="faucet-token-toggle" className="grid grid-cols-3 gap-2 p-1 rounded-lg bg-[#050505] border border-[#2A2411]">
              {["FMI", "USDT", "FBTC"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`faucet-token-${t.toLowerCase()}`}
                  onClick={() => { setToken(t); setResult(null); setError(""); }}
                  className={`py-2 rounded-md text-sm font-semibold transition-colors ${token === t ? "bg-[#D4AF37] text-black" : "text-zinc-400 hover:text-white"}`}
                >
                  {t}
                </button>
              ))}
            </div>

            <FaucetMonitor />

            <form onSubmit={handleRequest} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                  Wallet Address
                </label>
                <div className="flex gap-2">
                  <Input
                    data-testid={FAUCET.addressInput}
                    placeholder="0x..."
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="bg-[#050505] border-[#2A2411] text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#D4AF37]/50 font-mono text-sm"
                  />
                  {walletAddr && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fillWalletAddress}
                      className="border-[#2A2411] text-zinc-400 hover:text-white hover:bg-white/5 shrink-0 text-xs"
                    >
                      Paste
                    </Button>
                  )}
                </div>
              </div>

              <Button
                data-testid={FAUCET.requestBtn}
                type="submit"
                disabled={loading || waitSeconds > 0}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold h-12 rounded-md transition-transform active:scale-[0.98]"
              >
                <SubmitLabel loading={loading} waitSeconds={waitSeconds} token={token} amount={amount} />
              </Button>
            </form>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span data-testid={FAUCET.statusMessage}>{error}</span>
              </div>
            )}

            {result && (
              <motion.div
                {...RESULT_ANIM}
                className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 space-y-2"
              >
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle size={16} />
                  <span data-testid={FAUCET.statusMessage} className="text-sm font-medium">{result.message}</span>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Transaction Hash</p>
                  {result.isDemo ? (
                    <p data-testid={FAUCET.txHash} className="font-mono text-xs text-zinc-300 break-all">{result.txHash}</p>
                  ) : (
                    <Link to={`/tx/${result.txHash}`} data-testid={FAUCET.txHash} className="table-link font-mono text-xs break-all">
                      {result.txHash}
                    </Link>
                  )}
                </div>
                {result.isDemo && (
                  <Badge className="demo-badge mt-2">Demo Mode</Badge>
                )}
                {!result.isDemo && <AddTokenButtons />}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
